import React, { useEffect, useState, useRef } from "react";
import { fetchWooOrdersByPhonePaginated, cancelWooOrder } from "./action.ts";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Heading from "../../Components/heading.tsx";
import SupervisorHeading from "../../../WebApp/Components/Heading/heading.tsx";
import "./offers.scss";

interface Order {
  id: number;
  date_created: string;
  status: string;
  total: string;
  line_items: {
    name: string;
    quantity: number;
    total: string;
  }[];
}

interface YourOrdersProps {
  userPhone: string;
  onBack: () => void;
  userRole?: string;
}

const YourOrders: React.FC<YourOrdersProps> = ({
  userPhone,
  onBack,
  userRole,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState("all");
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<number | false>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

const isVendorSupervisor = ["vendor_supervisor", "vendor_company_admin", "client_company_admin"].includes(userRole);
  const handleOpenConfirm = (orderId: number) => {
    setSelectedOrderId(orderId);
    setOpenConfirm(true);
  };
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSelectedOrderId(null);
  };
  const handleConfirmCancel = async () => {
    if (!selectedOrderId) return;
    const response = await cancelWooOrder(selectedOrderId);
    if (response.success) {
      setOrders((prev) =>
        prev.map((o) =>
          o.id === selectedOrderId ? { ...o, status: "cancelled" } : o
        )
      );
    } else {
      alert("Failed to cancel order: " + response.error);
    }
    handleCloseConfirm();
  };
  const loadOrders = (pageToLoad: number, reset = false) => {
    const status = tabValue === "all" ? undefined : tabValue;
    if (reset) {
      setLoading(true);
      setError("");
    } else {
      setLoadingMore(true);
    }

    fetchWooOrdersByPhonePaginated(
      userPhone.trim(),
      pageToLoad,
      (data) => {
        setOrders((prev) =>
          reset ? data.orders : [...prev, ...data.orders]
        );
        setHasNextPage(data.hasNextPage);
        setLoading(false);
        setLoadingMore(false);
      },
      (err) => {
        setError("Error fetching orders.");
        setLoading(false);
        setLoadingMore(false);
      },
      8,
      status
    );
  };

  useEffect(() => {
    if (userPhone) {
      setPage(1);
      loadOrders(1, true);
    } else {
      setError("Phone number not found");
      setLoading(false);
    }
  }, [userPhone, tabValue]);
  // Auto-load next page if orders < screen height
  useEffect(() => {
    if (
      !loading &&
      hasNextPage &&
      containerRef.current &&
      containerRef.current.scrollHeight <= containerRef.current.clientHeight
    ) {
      loadOrders(page + 1);
      setPage((p) => p + 1);
    }
  }, [orders, loading, hasNextPage]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (!loadingMore && hasNextPage && scrollTop + clientHeight >= scrollHeight - 50) {
      loadOrders(page + 1);
      setPage((p) => p + 1);
    }
  };

  const renderOrders = (ordersToRender: Order[]) =>
    ordersToRender.map((order, index) => {
      const orderDate = new Date(order.date_created).toLocaleDateString();
      const itemNames = order.line_items.map((item) => item.name).join(", ");
      const formattedStatus =
        order.status.charAt(0).toUpperCase() + order.status.slice(1);

      return (
        <Accordion
          key={order.id}
          expanded={expandedOrder === order.id}
          onChange={() =>
            setExpandedOrder(expandedOrder === order.id ? false : order.id)
          }
          disableGutters
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" flexDirection="column" width="100%">
              <Box display="flex" justifyContent="space-between" fontSize={14}>
                <Typography fontWeight="bold">
                  {index + 1}. {itemNames}
                </Typography>
                <Typography color="primary" fontWeight="bold">
                  ₹{order.total}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontSize: 15 }} color="text.secondary">
                Status: {formattedStatus}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ bgcolor: "#f8f8f8" }}>
            <Typography sx={{ fontWeight: "bold", mb: 1 }}>Items:</Typography>
            {order.line_items.map((item, i) => (
              <Grid
                key={i}
                container
                spacing={1}
                sx={{ mb: 0.5, fontSize: "0.9rem" }}
              >
                <Grid item xs={5}>
                  <Typography>{item.name}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>Qty: {item.quantity}</Typography>
                </Grid>
                <Grid item xs={4} textAlign="right">
                  <Typography>₹{item.total}</Typography>
                </Grid>
              </Grid>
            ))}
            <Box mt={2}>
              <Typography><strong>Order ID:</strong> {order.id}</Typography>
              <Typography><strong>Order Date:</strong> {orderDate}</Typography>
              <Typography><strong>Status:</strong> {formattedStatus}</Typography>
              <Typography><strong>Total:</strong> ₹{order.total}</Typography>
            </Box>
            {order.status !== "cancelled" && order.status !== "completed" && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button variant="outlined" color="error" size="small" onClick={() => handleOpenConfirm(order.id)}>
                  Cancel Order
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  let content;
  if (isVendorSupervisor) {
    content = (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
              <Box
                sx={{
                  flexShrink: 0,
                  position: "sticky",
                  top: 10,
                  zIndex: 1100,
                }}
              >
        <SupervisorHeading title="Your Orders" closeIcon={false} backArrow onBack={onBack} className="no-margin-heading"/>
        </Box>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="All" value="all" />
          <Tab label="Processing" value="processing" />
          <Tab label="Completed" value="completed" />
          <Tab label="Cancelled" value="cancelled" />
        </Tabs>

        <Box
          ref={containerRef}
          onScroll={handleScroll}
          sx={{ flex: 1, overflowY: "auto", p: 2,pb: 10,  background: "#f9f9f9" }}
        >
          {loading ? (
            <Box textAlign="center" mt={2} mb={2}>
              <Typography >Loading orders…</Typography>
            </Box>
          ) : error ? (
            <Typography color="error" mt={2}>{error}</Typography>
          ) : orders.length === 0 ? (
            <Typography mt={2}>No orders found for "{tabValue}" status.</Typography>
          ) : (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
              gap: 2,
            }}>
              {renderOrders(orders)}
            </Box>
          )}
          
        {loadingMore && (
          <Box textAlign="center" mt={2}>
            <Typography >Loading orders…</Typography>
          </Box>
        )}
        {!hasNextPage && !loading && orders.length > 0 && (
          <Box textAlign="center" mt={2}>
            <Typography >No more orders</Typography>
          </Box>
        )}
        </Box>
      </Box>
    );
  }else {
  content = (
    <Container
      maxWidth="sm"
      sx={{ px: 2, pb: 2, margin: "0 auto" }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 1000,
          pt: "30px",  
          pb: "8px",   
        }}
      >
        <Heading title="Your Orders" onBackClick={onBack} />
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          <Tab label="Processing" value="processing" />
          <Tab label="Completed" value="completed" />
          <Tab label="Cancelled" value="cancelled" />
        </Tabs>
      </Box>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          overflowY: "auto",
          maxHeight: "calc(100vh - 150px)",
          pt: 2,
          pb: "90px",
          pr: 2,     
          mr: "-8px",     
        }}
      >
        {loading ? (
          <Box textAlign="center" mt={2} mb={2}>
            <Typography>Loading orders…</Typography>
          </Box>
        ) : error ? (
          <Typography color="error" mt={2}>
            {error}
          </Typography>
        ) : orders.length === 0 ? (
          <Typography mt={2}>
            No orders found for "{tabValue}" status.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            {renderOrders(orders)}
          </Box>
        )}

        {loadingMore && (
          <Box textAlign="center" mt={2}>
            <Typography>Loading orders…</Typography>
          </Box>
        )}

        {!hasNextPage && !loading && orders.length > 0 && (
          <Box textAlign="center" mt={2}>
            <Typography>No more orders</Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}

  return (
    <>
      {content}
      <Dialog open={openConfirm} onClose={handleCloseConfirm} fullWidth maxWidth="sm">
        {isVendorSupervisor ? (
          <SupervisorHeading title="Cancel Order" closeIcon={false} className="no-margin-heading" />
        ) : (
          <DialogTitle>Cancel Order</DialogTitle>
        )}
        <DialogContent>
          <DialogContentText>Are you sure you want to cancel this order?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>No</Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default YourOrders;
